/***
 * This file contains utility functions for working with files
 *
 * TODO :: Convert me to TypeScript - https://www.typescriptlang.org/
 */
import JSZip from "jszip";

const NEWLINE = "\n";
const COMMA = ",";

/**
 * Consumes a file and returns a promise that resolves to a FileReader
 * @param file File
 * @returns Promise<FileReader>
 */
const fileReader = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr);
    fr.onerror = (err) => reject(err);
    fr.readAsText(file);
  });

/**
 * Returns a file with the given content, name, and type
 * where type is a MIME type following the format: "text/plain"
 * See: https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types
 * @param content string
 * @param name string
 * @param mimeType string
 * @returns
 */
const fileWriter = (content, name, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  return new File([blob], name);
};

String.prototype.splitCSV = function () {
  const matches = this.match(/(\s*"[^"]+"\s*|\s*[^,]+|,)(?=,|$)/g);
  for (let n = 0; n < matches.length; ++n) {
    matches[n] = matches[n].trim();
    if (matches[n] === ",") matches[n] = "";
  }
  if (this[0] === ",") matches.unshift("");
  return matches;
};

/**
 * Convert DOS line endings to UNIX line endings
 * @param {*} str
 * @returns
 */
const normalizeLineEndings = (str) => str.replace(/\r\n|\r/g, NEWLINE);

/**
 * Remove HTML from file content
 * @param {*} html_str
 * @returns
 */
function stripHtml(html_str) {
  const doc = new DOMParser().parseFromString(html_str, "text/html");
  return doc.body.textContent || "";
}

/**
 * Parse a single CSV line into fields, handling quoted fields with commas and escaped quotes.
 * @param {string} line - A single line of CSV.
 * @returns {string[]} Array of field values.
 */
function parseCSVLine(line) {
  const fields = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote within quoted field
        field += '"';
        i++;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === COMMA && !inQuotes) {
      fields.push(field);
      field = "";
    } else {
      field += char;
    }
  }
  fields.push(field);
  return fields;
}

/**
 * Parse a CSV string into an array of objects.
 * @param {string} content - CSV content as a string.
 * @returns {object[]} Array of records as objects keyed by header names.
 */
const csvParser = (content) => {
  const htmlFree = stripHtml(content);
  const normalized = normalizeLineEndings(htmlFree);
  const lines = normalized.split(NEWLINE);
  // Remove empty lines
  const filtered = lines.filter((line) => line.trim() !== "");
  if (filtered.length === 0) return [];
  // Parse header
  const headers = parseCSVLine(filtered[0]);
  // Parse data rows
  return filtered.slice(1).map((line) => {
    const values = parseCSVLine(line);
    const obj = {};
    headers.forEach((header, idx) => {
      obj[header] = values[idx];
    });
    return obj;
  });
};

/**
 * Write an array of objects to a CSV file like string
 * @param {*} data
 * @param {*} delimiter Defaults to "," but may supply any valid delimiter
 */
const csvWriter = (data, delimiter = COMMA) => {
  const headers = Object.keys(data[0]);
  const lines = data.map((obj) => {
    const values = headers.map((header) => {
      let value = obj[header] || "";
      // Escape quotes and wrap in quotes if necessary
      if (typeof value === "string") {
        value = value.replace(/"/g, '""'); // Escape double quotes
        if (
          value.includes(delimiter) ||
          value.includes('"') ||
          value.includes("\n")
        ) {
          value = `"${value}"`; // Wrap in quotes if it contains delimiter, quotes, or newlines
        }
      }
      return value;
    });
    return values.join(delimiter);
  });
  return headers.join(delimiter) + NEWLINE + lines.join(NEWLINE);
};

/**
 * Enriches a CSV file like string with the given content
 * @param content
 * @returns File
 */
const csvFileEnricher = (content, enrichedContent) => {
  const parsed = csvParser(content);
  const enriched = parsed.map((obj) => {
    return { ...obj, ...enrichedContent };
  });
  return csvWriter(enriched);
};

/**
 * Checks if a file is HTML by examining both extension and MIME type.
 * Uses defense-in-depth: checks BOTH extension AND MIME type to prevent
 * rename attacks where malicious.html is renamed to innocent.pdf.
 *
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is HTML
 */
const isHtmlFile = (file) => {
  const hasHtmlExtension =
    file.name &&
    (file.name.toLowerCase().endsWith(".html") ||
      file.name.toLowerCase().endsWith(".htm"));

  const hasHtmlMimeType =
    file.type &&
    (file.type === "text/html" || file.type === "application/xhtml+xml");

  return hasHtmlExtension || hasHtmlMimeType;
};

/**
 * Takes an array of files and returns a zip file packaged using DEFLATE as the default compression algorithm with maximum compression
 * @param files File[]
 * @returns Blob
 */
const zipFiles = async (files, compression, compressionLevel) => {
  const zip = new JSZip();
  files.forEach((file) => {
    zip.file(file.name, file);
  });
  return await zip.generateAsync({
    type: "blob",
    compression: compression || "DEFLATE",
    compressionOptions: { level: compressionLevel || 9 },
  });
};

export {
  csvFileEnricher,
  csvParser,
  csvWriter,
  fileReader,
  fileWriter,
  isHtmlFile,
  zipFiles,
};
